import { useEffect, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { api, type Certificate } from '../api.ts';

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * The learner's certificates.
 *
 * The number and the check link are the point of this screen: a certificate is only worth
 * anything if somebody else can confirm it, so both are shown in full and made easy to copy
 * rather than buried behind a download.
 */
export function CertificatesPage(): JSX.Element {
  const [certificates, setCertificates] = useState<Certificate[] | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  useEffect(() => {
    api
      .myCertificates()
      .then(setCertificates)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'Не удалось загрузить сертификаты'),
      );
  }, []);

  return (
    <>
      <div className="page-head">
        <Link to="/" className="back">
          ← Мои курсы
        </Link>
        <h1>Сертификаты</h1>
      </div>

      {error ? <p className="error">{error}</p> : null}
      {certificates === null && !error ? <p className="muted">Загружаем…</p> : null}

      {certificates?.length === 0 ? (
        <div className="notice">
          Здесь появятся сертификаты за пройденные курсы. Сертификат выдаётся, когда курс пройден
          полностью — и только если преподаватель его предусмотрел.
        </div>
      ) : null}

      {(certificates ?? []).map((certificate) => (
        <section
          key={certificate.id}
          className={`certcard${certificate.is_valid ? '' : ' certcard--void'}`}
        >
          <div className="certcard__head">
            <h2>{certificate.course_title}</h2>
            {certificate.revoked_at ? (
              <span className="badge">отозван</span>
            ) : certificate.is_expired ? (
              <span className="badge badge--wait">срок истёк</span>
            ) : (
              <span className="badge badge--live">действителен</span>
            )}
          </div>

          <div className="certcard__number">{certificate.number}</div>

          <dl className="certcard__facts">
            <div>
              <dt>Выдан</dt>
              <dd>{formatDate(certificate.issued_at)}</dd>
            </div>
            {certificate.expires_at ? (
              <div>
                <dt>Действителен до</dt>
                <dd>{formatDate(certificate.expires_at)}</dd>
              </div>
            ) : null}
            {certificate.score !== null ? (
              <div>
                <dt>Итоговый балл</dt>
                <dd>{Math.round(certificate.score)}%</dd>
              </div>
            ) : null}
          </dl>

          {certificate.revoke_reason ? (
            <div className="notice notice--accent">Причина отзыва: {certificate.revoke_reason}</div>
          ) : null}

          <div className="row">
            <a
              className="btn btn--small"
              href={api.certificatePdfUrl(certificate.id)}
              /* Same origin, and the server sets the filename — the browser does the rest. */
              download
            >
              Скачать PDF
            </a>
            <Link to={`/verify/${certificate.number}`} className="btn btn--ghost btn--small">
              Страница проверки
            </Link>
            <button
              type="button"
              className="btn btn--ghost btn--small"
              onClick={() => {
                void navigator.clipboard
                  .writeText(certificate.verify_url)
                  .then(() => setCopied(certificate.id))
                  .catch(() => undefined);
              }}
            >
              {copied === certificate.id ? 'Ссылка скопирована' : 'Скопировать ссылку'}
            </button>
          </div>
        </section>
      ))}
    </>
  );
}
